import React, { useRef } from 'react';

import * as NavigationMenuPrimitive from '@radix-ui/react-navigation-menu';
import { Loader2Icon, PartyPopperIcon } from 'lucide-react';
import { useQuery } from '@apollo/client';

import {
  NavigationMenuContent,
  NavigationMenuItem,
} from '@sandtech-hub-monorepo/ui-kit';
import {
  GetViewerNotificationsDocument,
  NotificationEdge,
} from '@sandtech-hub-monorepo/ehub-client';

import { NotificationItem } from './NotificationItem';
import {
  BellIcon,
  BellWithCircleIcon,
} from '@sandtech-hub-monorepo/ui-kit-site';
import { NOTIFICATIONS_MENU_ITEM_VALUE } from '../utils';
import { NotificationItemSkeleton } from './NotificationItemSkeleton';

const notificationsPageCount = 5;

export const NotificationsCenter = ({
  className,
  sdk,
}: {
  className?: string;
  sdk: boolean;
}) => {
  const notificationsContainerRef = useRef(null);

  const { data, loading, fetchMore } = useQuery(
    GetViewerNotificationsDocument,
    {
      variables: { first: notificationsPageCount },
    }
  );

  const handleLoadMore = async () => {
    const lastCursor =
      data?.viewer.notifications.edges[
        data?.viewer.notifications.edges.length - 1
      ].cursor || null;

    if (lastCursor) {
      await fetchMore({
        variables: {
          first: notificationsPageCount,
          after: lastCursor,
        },
      });
    }
  };

  const handleScroll = () => {
    const div = notificationsContainerRef.current;

    if (div) {
      const { scrollHeight, scrollTop, clientHeight } = div;
      const isScrolledBottom = scrollHeight - scrollTop - clientHeight <= 1;
      if (isScrolledBottom && data?.viewer.notifications.pageInfo.hasNextPage) {
        handleLoadMore();
      }
    }
  };

  return (
    <NavigationMenuItem
      className={className}
      value={NOTIFICATIONS_MENU_ITEM_VALUE}
    >
      <NavigationMenuPrimitive.Trigger
        onPointerMove={(event) => event.preventDefault()}
        onPointerLeave={(event) => event.preventDefault()}
      >
        {loading ||
        data?.viewer.notifications.edges.every((nEdge) => nEdge.node.seen) ? (
          <BellIcon className="[&_path]:stroke-current" />
        ) : (
          <BellWithCircleIcon className="[&_path]:stroke-current" />
        )}
      </NavigationMenuPrimitive.Trigger>
      <NavigationMenuContent
        ref={notificationsContainerRef}
        className="h-[60vh] sm:h-[640px] w-[calc(100vw-3rem)] xs:w-[400px] md:w-[400px] p-4 overflow-auto overscroll-y-contain"
        onPointerMove={(event) => event.preventDefault()}
        onPointerLeave={(event) => event.preventDefault()}
        onScroll={(e) => {
          handleScroll();
          e.preventDefault();
        }}
      >
        <div className="overflow-auto">
          {loading ? (
            <Loader2Icon className="animate-spin" />
          ) : data?.viewer?.notifications.edges.length ? (
            <>
              <ul className="flex flex-col gap-4">
                {data.viewer.notifications.edges.map((notification) => (
                  <NotificationItem
                    notification={notification.node}
                    key={notification.node.id}
                    sdk={sdk}
                  />
                ))}
              </ul>
              {data.viewer.notifications.pageInfo.hasNextPage && (
                <NotificationItemSkeleton />
              )}
            </>
          ) : (
            <div className="flex h-32 gap-2 items-center justify-center ">
              <PartyPopperIcon /> You're all caught up!
            </div>
          )}
        </div>
      </NavigationMenuContent>
    </NavigationMenuItem>
  );
};
